// Shared browser/server validator. Only explicitly listed public settings are accepted.
export function validateRuntimeConfig(config, schema, hostname) {
  if (
    !config ||
    config.schemaVersion !== schema.version ||
    !['local', 'staging', 'production'].includes(config.environment) ||
    !Array.isArray(config.allowedHosts) ||
    !config.allowedHosts.length ||
    !config.allowedHosts.every(
      host => typeof host === 'string' && /^[a-z0-9.-]+$/.test(host),
    ) ||
    !config.values ||
    typeof config.values !== 'object' ||
    Array.isArray(config.values)
  ) {
    throw new Error('Invalid runtime configuration envelope');
  }
  if (
    Object.keys(config).some(
      key => !['schemaVersion', 'environment', 'allowedHosts', 'values'].includes(key),
    )
  ) {
    throw new Error('Unexpected runtime configuration field');
  }
  if (hostname && !config.allowedHosts.includes(hostname))
    throw new Error('Runtime configuration host mismatch');
  for (const key of Object.keys(config.values)) {
    if (!schema.keys.includes(key) || typeof config.values[key] !== 'string')
      throw new Error('Unexpected public setting: ' + key);
  }
  for (const key of schema.required) {
    if (!config.values[key]) throw new Error('Missing public setting: ' + key);
  }
  for (const key of schema.booleans) {
    if (config.values[key] && !['true', 'false'].includes(config.values[key]))
      throw new Error('Invalid boolean: ' + key);
  }
  for (const key of schema.rates) {
    const value = config.values[key];
    if (
      value &&
      (!Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 1)
    )
      throw new Error('Invalid rate: ' + key);
  }
  const recaptchaMode = config.values.REACT_APP_RECAPTCHA_MODE || 'enabled';
  if (!['enabled', 'disabled'].includes(recaptchaMode))
    throw new Error('Invalid reCAPTCHA mode');
  const isolatedStaging =
    config.environment === 'staging' &&
    config.allowedHosts.length === 1 &&
    config.allowedHosts[0] === 'staging.signaturely.dev' &&
    [
      'REACT_APP_API_BASE_URL',
      'REACT_APP_FRONTEND_URL',
      'REACT_APP_FRONTEND_URL_VERSION_B',
    ].every(key => config.values[key] === 'https://staging.signaturely.dev');
  const isolatedLocal =
    config.environment === 'local' &&
    config.values.REACT_APP_NODE_ENV === 'development' &&
    config.allowedHosts.length > 0 &&
    config.allowedHosts.every(host =>
      ['127.0.0.1', 'localhost', '[::1]'].includes(host),
    ) &&
    [
      'REACT_APP_API_BASE_URL',
      'REACT_APP_FRONTEND_URL',
      'REACT_APP_FRONTEND_URL_VERSION_B',
    ].every(key => {
      try {
        const url = new URL(config.values[key]);
        return (
          ['http:', 'https:'].includes(url.protocol) &&
          config.allowedHosts.includes(url.hostname) &&
          !url.username &&
          !url.password &&
          !url.search &&
          !url.hash &&
          url.pathname === '/'
        );
      } catch (_) {
        return false;
      }
    });
  if (recaptchaMode === 'disabled' && !isolatedStaging && !isolatedLocal)
    throw new Error(
      'Disabled reCAPTCHA requires isolated Local or Staging configuration',
    );
  if (!(Number(config.values.REACT_APP_IDLE_TIMEOUT_MINUTES) > 0))
    throw new Error('Invalid idle timeout');
  for (const key of [
    'REACT_APP_API_BASE_URL',
    'REACT_APP_FRONTEND_URL',
    'REACT_APP_FRONTEND_URL_VERSION_B',
  ]) {
    const url = new URL(config.values[key]);
    if (
      url.username ||
      url.password ||
      (url.protocol !== 'https:' &&
        !(
          config.environment === 'local' &&
          url.protocol === 'http:' &&
          ['localhost', '127.0.0.1'].includes(url.hostname)
        ))
    )
      throw new Error('Invalid public URL: ' + key);
    if (key !== 'REACT_APP_API_BASE_URL' && !config.allowedHosts.includes(url.hostname))
      throw new Error('Frontend URL host mismatch');
  }
  return config;
}
